// src/pages/Dashboard/DashboardView.tsx
import React from 'react'
import Sidebar from '../../components/Layout/Sidebar'
import Header from '../../components/Layout/Header'
import StatsCards from '../../components/Dashboard/StatsCards'
import RecentTransactions from '../../components/Dashboard/RecentTransactions'
import {DashboardStats} from '../../types'
import {formatUSD} from '../../utils/helpers'
import {Wallet, TrendingUp, Activity} from 'lucide-react'

interface DashboardViewProps {
    stats: DashboardStats | null
    wallets: {alfa: number; touch: number}
    loading: boolean
    sidebarOpen: boolean
    toggleSidebar: () => void
}

export const DashboardView: React.FC<DashboardViewProps> = ({
    stats,
    wallets,
    loading,
    sidebarOpen,
    toggleSidebar
}) => {
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header toggleSidebar={toggleSidebar} />

            <Sidebar
                isOpen={sidebarOpen}
                onClose={() => toggleSidebar()}
                isMobile={true}
            />

            <div className="hidden lg:block">
                <Sidebar isMobile={false} />
            </div>

            <main className="pt-16 lg:pr-64">
                <div className="p-4 lg:p-6">
                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-2xl lg:text-3xl font-bold text-gray-800">
                            مرحباً بك في لوحة التحكم
                        </h1>
                        <p className="text-gray-500 mt-1">
                            آخر تحديث: {new Date().toLocaleString('ar-LB')}
                        </p>
                    </div>

                    {/* Wallet Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                        {/* Alfa Wallet */}
                        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl shadow-lg p-6 text-white transform hover:scale-105 transition-transform duration-200">
                            <div className="flex items-center justify-between mb-4">
                                <div className="bg-white/20 p-3 rounded-xl">
                                    <Wallet className="w-6 h-6" />
                                </div>
                                <span className="text-sm bg-white/30 px-3 py-1 rounded-full">Alfa</span>
                            </div>
                            <div>
                                <p className="text-blue-200 text-sm mb-1">رصيد المحفظة</p>
                                <p className="text-3xl font-bold">{formatUSD(wallets.alfa)}</p>
                                <p className="text-blue-200 text-sm mt-2">آخر تحديث: مباشر</p>
                            </div>
                        </div>

                        {/* Touch Wallet */}
                        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl shadow-lg p-6 text-white transform hover:scale-105 transition-transform duration-200">
                            <div className="flex items-center justify-between mb-4">
                                <div className="bg-white/20 p-3 rounded-xl">
                                    <Wallet className="w-6 h-6" />
                                </div>
                                <span className="text-sm bg-white/30 px-3 py-1 rounded-full">Touch</span>
                            </div>
                            <div>
                                <p className="text-green-200 text-sm mb-1">رصيد المحفظة</p>
                                <p className="text-3xl font-bold">{formatUSD(wallets.touch)}</p>
                                <p className="text-green-200 text-sm mt-2">آخر تحديث: مباشر</p>
                            </div>
                        </div>
                    </div>

                    {/* Today's Summary */}
                    {stats?.todayStats && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <Activity className="w-5 h-5 text-blue-500" />
                                ملخص اليوم
                            </h2>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">عدد المعاملات</p>
                                    <p className="text-2xl font-bold text-gray-800">{stats.todayStats.count || 0}</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">حجم USD</p>
                                    <p className="text-2xl font-bold text-green-600">{formatUSD(stats.todayStats.usd || 0)}</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">حجم LBP</p>
                                    <p className="text-2xl font-bold text-purple-600">{(stats.todayStats.lbp || 0).toLocaleString()} LL</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-sm text-gray-500">الرسوم</p>
                                    <p className="text-2xl font-bold text-orange-600">{formatUSD(stats.todayStats.fees || 0)}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Stats Cards - المكون الجديد */}
                    <div className="mb-8">
                        <StatsCards stats={stats} />
                    </div>

                    {/* Recent Transactions */}
                    <div className="mb-8">
                        <RecentTransactions transactions={stats?.recentTransactions || []} />
                    </div>

                    {/* Top Users */}
                    {stats?.topUsers && stats.topUsers.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-blue-500" />
                                أكثر المستخدمين نشاطاً
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {stats.topUsers.map((user: any, index: number) => (
                                    <div key={user.user_id} className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${index === 0 ? 'bg-yellow-500' :
                                                index === 1 ? 'bg-gray-400' :
                                                    index === 2 ? 'bg-orange-600' : 'bg-blue-500'
                                                }`}>
                                                {index + 1}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{user.phone || 'مستخدم'}</p>
                                                <p className="text-xs text-gray-500">{user.transactions || 0} معاملة</p>
                                            </div>
                                        </div>
                                        <p className="text-sm font-bold text-green-600">{formatUSD(user.volume || 0)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    )
}